import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { api, formatINR, monthLabel } from "../services/mmcapi";

const formatDateTime = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const formatDate = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-IN", { dateStyle: "medium" });
};

const STATUS_LABELS = {
  pending: "Pending",
  overdue: "Overdue",
  paid: "Paid",
  cancelled: "Cancelled"
};

const statusBadgeStyle = (s) => {
  if (s === "paid") return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  if (s === "overdue") return { background: "rgba(239,68,68,0.18)", color: "#dc2626" };
  if (s === "cancelled") return { background: "rgba(107,114,128,0.15)", color: "#4b5563" };
  return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
};

const DetailRow = ({ label, value }) => (
  <div className="row py-2 border-bottom" style={{ margin: 0 }}>
    <div className="col-12 col-sm-4 text-muted" style={{ fontSize: 13, fontWeight: 600 }}>
      {label}
    </div>
    <div className="col-12 col-sm-8" style={{ fontSize: 14 }}>
      {value != null && value !== "" ? value : "—"}
    </div>
  </div>
);

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8
    }}
  >
    {children}
  </div>
);

const MaintenanceChargeView = () => {
  const navigate = useNavigate();
  const { chargeId: chargeIdParam } = useParams();
  const chargeId = useMemo(() => {
    try {
      return chargeIdParam ? decodeURIComponent(String(chargeIdParam)).trim() : "";
    } catch {
      return String(chargeIdParam || "").trim();
    }
  }, [chargeIdParam]);

  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(null);

  useEffect(() => {
    if (!chargeId) {
      toast.error("Invalid maintenance charge link.");
      navigate("/maintenance-charges", { replace: true });
    }
  }, [chargeId, navigate]);

  const fetchCharge = useCallback(async () => {
    if (!chargeId) return;
    try {
      const data = await api("admin.getMMCharge", { chargeId });
      setCharge(data.charge || null);
    } catch (e) {
      toast.error(e.message);
      setCharge(null);
    } finally {
      setLoading(false);
    }
  }, [chargeId]);

  useEffect(() => {
    fetchCharge();
  }, [fetchCharge]);

  if (!chargeId) return null;

  const st = charge
    ? charge.status === "pending" && charge.isOverdue
      ? "overdue"
      : charge.status
    : "pending";

  return (
    <div className="employee-form tenant-report">
      <div className="content-header">
        <div className="container-fluid">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{ gap: 12 }}
          >
            <div>
              <h1 className="m-0 txt_lft">Maintenance charge details</h1>
              <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>
                Review the charge, who it is billed to, and its payment status
              </p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/maintenance-charges" className="btn btn-outline-secondary">
                Back to list
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !charge ? (
            <div className="card">
              <div className="card-body text-muted">Loading maintenance charge…</div>
            </div>
          ) : charge ? (
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <h2 className="h4 mb-1">
                    {monthLabel(charge.month)}
                    {charge.isAdvance ? (
                      <span
                        className="badge"
                        style={{
                          marginLeft: 10,
                          background: "rgba(59,130,246,0.15)",
                          color: "#2563eb",
                          fontSize: 11
                        }}
                      >
                        ADVANCE
                      </span>
                    ) : null}
                  </h2>
                </div>

                <SectionTitle first>Charge</SectionTitle>
                <div className="border rounded overflow-hidden mb-0">
                  <DetailRow label="Month" value={monthLabel(charge.month)} />
                  <DetailRow label="Amount" value={formatINR(charge.amount)} />
                  <DetailRow label="Due date" value={formatDate(charge.dueDate)} />
                  <DetailRow
                    label="Status"
                    value={
                      <span
                        className="badge"
                        style={{
                          padding: "0.35rem 0.65rem",
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 11,
                          ...statusBadgeStyle(st)
                        }}
                      >
                        {(STATUS_LABELS[st] || st).toUpperCase()}
                      </span>
                    }
                  />
                  <DetailRow label="Paid on" value={formatDateTime(charge.paidAt)} />
                  <DetailRow label="Payment ID" value={charge.razorpayPaymentId} />
                  <DetailRow label="Order ID" value={charge.razorpayOrderId} />
                </div>

                <SectionTitle>Billed to</SectionTitle>
                <div className="border rounded overflow-hidden mb-0">
                  <DetailRow label="Role" value={charge.billedTo} />
                  <DetailRow label="Name" value={charge.payer?.name} />
                  <DetailRow label="Email" value={charge.payer?.email} />
                  <DetailRow label="Mobile" value={charge.payer?.phone} />
                </div>

                <SectionTitle>Property</SectionTitle>
                <div className="border rounded overflow-hidden mb-0">
                  <DetailRow label="Block" value={charge.block} />
                  <DetailRow label="Flat no." value={charge.flatNumber} />
                  <DetailRow label="Owner name" value={charge.owner?.name} />
                  <DetailRow label="Owner email" value={charge.owner?.email} />
                  <DetailRow label="Owner mobile" value={charge.owner?.phone} />
                </div>

                {charge.isAdvance && charge.advance ? (
                  <>
                    <SectionTitle>Advance payment</SectionTitle>
                    <div className="border rounded overflow-hidden mb-0">
                      <DetailRow
                        label="Months paid"
                        value={(charge.advance.months || []).map(monthLabel).join(", ")}
                      />
                      <DetailRow
                        label="Amount per month"
                        value={formatINR(charge.advance.amountPerMonth)}
                      />
                      <DetailRow
                        label="Total paid"
                        value={formatINR(charge.advance.totalAmount)}
                      />
                      <DetailRow label="Paid on" value={formatDateTime(charge.advance.paidAt)} />
                    </div>
                  </>
                ) : null}

                <SectionTitle>Record</SectionTitle>
                <div className="border rounded overflow-hidden mb-0">
                  <DetailRow label="Generated by" value={charge.generatedBy} />
                  <DetailRow label="Created at" value={formatDateTime(charge.createdAt)} />
                  <DetailRow label="Updated at" value={formatDateTime(charge.updatedAt)} />
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-muted">
                Could not load this maintenance charge.&nbsp;
                <Link to="/maintenance-charges">Return to list</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceChargeView;